import { useCallback } from "react";

import { ICharContact, ICharContactFieldVisibility } from "store/types";

import { defaultContact, State } from "..";

interface AcceptParams {
  setState: React.Dispatch<React.SetStateAction<State>>;
}

interface Result {
  updateContact: (id: number | string, key: keyof ICharContact, value: string) => void;
  updateContactVisibility: (id: number | string, key: keyof ICharContactFieldVisibility) => void;
  addContact: () => void;
  deleteContact: (id: number | string) => void;
}

type useContactHooks = (params: AcceptParams) => Result;

export const useContactHooks: useContactHooks = ({ setState }) => {
  const deleteContact = useCallback<Result["deleteContact"]>(
    (id) => {
      setState((prev) => ({
        ...prev,
        contact: prev.contact.map((contact) => {
          if (contact.id !== id) return contact;

          if (contact.is_deleted) {
            const rest = { ...contact };
            delete rest.is_deleted;
            return rest;
          }

          return { ...contact, is_deleted: true };
        })
      }));
    },
    [setState]
  );

  const addContact = useCallback<Result["addContact"]>(() => {
    setState((prevState) => ({
      ...prevState,
      contact: [...prevState.contact, { ...defaultContact, id: crypto.randomUUID() }]
    }));
  }, [setState]);

  const updateContact = useCallback<Result["updateContact"]>(
    (id, key, value) => {
      setState((prevState) => ({
        ...prevState,
        contact: prevState.contact.map((contact) => (contact.id === id ? { ...contact, [key]: value } : contact))
      }));
    },
    [setState]
  );

  const updateContactVisibility = useCallback<Result["updateContactVisibility"]>(
    (id, key) => {
      setState((prevState) => ({
        ...prevState,
        contact: prevState.contact.map((contact) =>
          contact.id === id
            ? {
                ...contact,
                is_show: {
                  ...contact.is_show,
                  [key]: !contact.is_show[key]
                }
              }
            : contact
        )
      }));
    },
    [setState]
  );

  return {
    updateContact,
    updateContactVisibility,
    addContact,
    deleteContact
  };
};
